import { ComponentProps, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePathname, useSearchParams } from '@/lib/router';
import ClockIcon from '@ingka/ssr-icon/paths/clock';
import CoworkerIcon from '@ingka/ssr-icon/paths/co-worker';
import TrolleyIcon from '@ingka/ssr-icon/paths/furniture-trolley-loaded';
import AllenKeyIcon from '@ingka/ssr-icon/paths/allen-key';
import ListIcon from '@ingka/ssr-icon/paths/list';
import HomeIcon from '@ingka/ssr-icon/paths/home';
import {
  getCheckoutPath,
  getHomePath,
  getInventoryPath,
  getManagerOrdersActivePath,
  getManagerOrdersPreviousPath,
  getOrdersActivePath,
  getOrdersPreviousPath,
  getShopPath,
} from '@/lib/utils/router';
import {
  ItemStatus,
  OrderStatus,
  OrderScope,
} from '@/redux/back-dashboard-api';
import { useUser } from '@/redux/features/session/hooks';
import { useOrdersByScope } from '@/redux/back-dashboard-api/hooks';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import Skeleton from '@ingka/skeleton';
import {
  CATEGORIES,
  ItemCategory,
} from '@pcmp-checka/lib-services-common/domain/models/db-item';
import { Navigation } from '@pcmp/react-coworker-components';
import { useMenuContext } from './context';
import { CATEGORIES_ICONS } from '@/lib/constants';
import { getItemCategoryI18nKey } from '@/components/badges/item-category';

const { SideItemContainer: _SideItemContainer } = Navigation;

const SideItemContainer: FC<ComponentProps<typeof _SideItemContainer>> = (
  props
) => <_SideItemContainer className="px-4" {...props} />;

const isCategoryActive = (
  category: Omit<ItemCategory, 'type'>,
  selected?: string | null
): boolean => {
  if (selected === `${category._id}`) return true;

  return (
    category.subcategories?.some((subcategory) =>
      isCategoryActive(subcategory, selected)
    ) ?? false
  );
};

export const CategoryTree: FC<{
  parentCategory?: Omit<ItemCategory, 'type'>;
  category: Omit<ItemCategory, 'type'>;
  selected?: string | null;
}> = ({ parentCategory, category, selected }) => {
  // Hooks
  const { t } = useTranslation();
  const { goTo } = useMenuContext();
  // Data
  const { data: location } = useActiveLocation();
  const isFirstLevel = !parentCategory;
  const isActive = isCategoryActive(category, selected) && !isFirstLevel;

  return (
    <SideItemContainer
      bold={isFirstLevel}
      icon={CATEGORIES_ICONS[category._id]}
      title={t(getItemCategoryI18nKey(category._id))}
      active={isActive}
      expanded={isActive}
      onClick={
        category.subcategories
          ? undefined
          : goTo(
              getShopPath(location?.slug, {
                category: category._id,
              })
            )
      }
    >
      {category.subcategories?.map((subcategory) => (
        <CategoryTree
          parentCategory={category}
          category={subcategory}
          selected={selected}
          key={subcategory._id}
        />
      ))}
    </SideItemContainer>
  );
};

export const MainSideMenu: FC = () => {
  // Hooks
  const { t } = useTranslation();
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const { goTo } = useMenuContext();
  // Data
  const user = useUser();
  const { data: location } = useActiveLocation();
  const { data: ordersResponse } = useOrdersByScope(OrderScope.Me, user.email, {
    locationId: location?._id,
    skip: !location?._id || !user.email,
  });
  const { data: managerOrdersResponse } = useOrdersByScope(
    OrderScope.Coworkers,
    user.email,
    {
      locationId: location?._id,
      skip: !user.isManager || !location?._id || !user.email,
    }
  );
  const counter = useMemo(
    () => ({
      completed:
        ordersResponse?.filter(({ status }) => status === OrderStatus.Completed)
          .length || 0,
      pending:
        ordersResponse?.filter(({ status }) => status === OrderStatus.Pending)
          .length || 0,
      inventory:
        ordersResponse
          ?.flatMap((order) => order.items)
          .filter((item) =>
            [ItemStatus.Collected, ItemStatus.Recalled].includes(
              item.lastStatus
            )
          ).length || 0,
    }),
    [ordersResponse]
  );
  const managerCounter = useMemo(
    () => ({
      completed:
        managerOrdersResponse?.filter(
          ({ status }) => status === OrderStatus.Completed
        ).length || 0,
      pending:
        managerOrdersResponse?.filter(
          ({ status }) => status === OrderStatus.Pending
        ).length || 0,
    }),
    [managerOrdersResponse]
  );

  return (
    <>
      <SideItemContainer
        icon={HomeIcon}
        title={t('routes.home')}
        active={pathname === getHomePath()}
        onClick={goTo(getHomePath())}
      />

      <SideItemContainer
        icon={AllenKeyIcon}
        label={counter.inventory}
        title={t('routes.inventory')}
        active={pathname === getInventoryPath()}
        onClick={goTo(getInventoryPath())}
      />

      <SideItemContainer
        icon={ClockIcon}
        label={counter.pending}
        title={t('routes.ordersActive')}
        active={
          searchParams?.get('scope') === OrderScope.Me &&
          searchParams?.get('status') === OrderStatus.Pending
        }
        onClick={goTo(getOrdersActivePath())}
      />

      <SideItemContainer
        icon={TrolleyIcon}
        label={counter.completed}
        title={t('routes.ordersPrevious')}
        active={
          searchParams?.get('scope') === OrderScope.Me &&
          searchParams?.get('status') === OrderStatus.Completed
        }
        onClick={goTo(getOrdersPreviousPath())}
      />

      {user.isManager && (
        <SideItemContainer
          icon={CoworkerIcon}
          title={t('routes.coworkerOrders')}
          active={searchParams?.get('scope') === OrderScope.Coworkers}
          expanded={searchParams?.get('scope') === OrderScope.Coworkers}
        >
          <SideItemContainer
            icon={ClockIcon}
            label={managerCounter.pending}
            title={t('routes.ordersActive')}
            active={
              searchParams?.get('scope') === OrderScope.Coworkers &&
              searchParams?.get('status') === OrderStatus.Pending
            }
            onClick={goTo(getManagerOrdersActivePath())}
          />

          <SideItemContainer
            icon={TrolleyIcon}
            label={managerCounter.completed}
            title={t('routes.ordersPrevious')}
            active={
              searchParams?.get('scope') === OrderScope.Coworkers &&
              searchParams?.get('status') === OrderStatus.Completed
            }
            onClick={goTo(getManagerOrdersPreviousPath())}
          />
        </SideItemContainer>
      )}
    </>
  );
};

export const WebshopSideMenu: FC = () => {
  // Hooks
  const { t } = useTranslation();
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const { goTo } = useMenuContext();
  // Data
  const { data: location } = useActiveLocation();

  if (!location)
    return Array.from({ length: 3 })
      .fill(0)
      .map((_, index) => (
        <Skeleton key={index} height="40px" className="my-2" />
      ));

  return (
    <>
      <SideItemContainer
        icon={AllenKeyIcon}
        title={t('label.allItems')}
        active={
          pathname === getShopPath(location.slug) &&
          !searchParams?.get('category')
        }
        onClick={goTo(getShopPath(location.slug))}
      />

      <SideItemContainer
        icon={HomeIcon}
        title={t('label.checkout')}
        active={pathname.includes(getCheckoutPath())}
        onClick={goTo(getCheckoutPath())}
      />

      <SideItemContainer
        icon={ListIcon}
        title={t('label.categories')}
        expanded={!!searchParams?.get('category')}
      >
        {CATEGORIES.map((category) => (
          <CategoryTree
            key={category._id}
            category={category}
            selected={searchParams?.get('category')}
          />
        ))}
      </SideItemContainer>
    </>
  );
};
